'use client';

import Link from 'next/link';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';
import { useEffect, useRef, useState } from 'react';

import { isSidebarActiveAtom } from 'Atoms';
import styles from './Header.component.module.css';

import { useHeader } from './UseHeader';
import { usePathname } from 'next/navigation';

type HeaderLink = {
  label: string;
  url: string;
  childLinks?: HeaderLink[];
};

const ExpandMore = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="currentcolor"
    height="48"
    viewBox="0 -960 960 960"
    width="48"
    className={className}
    aria-hidden="true"
  >
    <path d="M480-371.923 267.692-584.231l22.231-22.231L480-416.154l190.077-190.077L692.308-584 480-371.923Z" />
  </svg>
);

export const HeaderWrapper = ({ children }) => {
  const path = usePathname();
  const { isAtTop } = useHeader();

  const isHomepage = path === '/' || path?.includes('/home');

  const isSidebarActive = useAtomValue(isSidebarActiveAtom);

  return (
    <div className={styles.wrapper} data-is-homepage={isHomepage && isAtTop} data-is-sidebar-active={isSidebarActive}>
      {children}
    </div>
  );
};

export const HeadeMenuButton = () => {
  const setIsSidebarActive = useUpdateAtom(isSidebarActiveAtom);

  const toggleSidebar = () => setIsSidebarActive((x) => !x);

  return (
    <button type="button" className={styles.menuButton} onClick={toggleSidebar} aria-label="Toggle navigation menu">
      <i className="fal fa-bars" />
    </button>
  );
};

export const DesktopNavigation = ({ links = [] }: { links?: HeaderLink[] }) => {
  const path = usePathname();
  const menuRef = useRef<HTMLDivElement>(null);
  const previousPathRef = useRef(path);
  const [expandedTopLevel, setExpandedTopLevel] = useState('');
  const [expandedSecondLevels, setExpandedSecondLevels] = useState<Set<string>>(() => new Set());

  const isCurrentSection = (url) => (url ? new RegExp(url, 'gi').test(path || '') : false);

  const toggleTopLevel = (label: string) => {
    setExpandedTopLevel((current) => (current === label ? '' : label));
  };

  const toggleSecondLevel = (key: string) => {
    setExpandedSecondLevels((current) => {
      const next = new Set(current);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  useEffect(() => {
    if (previousPathRef.current !== path) {
      previousPathRef.current = path;
      setExpandedTopLevel('');
      setExpandedSecondLevels(new Set());
    }
  }, [path]);

  useEffect(() => {
    const closeMenu = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setExpandedTopLevel('');
        setExpandedSecondLevels(new Set());
      }
    };

    document.addEventListener('pointerdown', closeMenu);
    return () => document.removeEventListener('pointerdown', closeMenu);
  }, []);

  return (
    <div className={styles.menu} ref={menuRef}>
      {links.map((topLevelLink) => {
        const hasChildren = !!topLevelLink.childLinks?.length;
        const isExpanded = expandedTopLevel === topLevelLink.label;

        return (
          <div
            className={styles.menuItem}
            data-is-active={isCurrentSection(topLevelLink.url)}
            data-is-expanded={isExpanded}
            key={topLevelLink.label}
          >
            {hasChildren ? (
              <button type="button" onClick={() => toggleTopLevel(topLevelLink.label)} aria-expanded={isExpanded}>
                {topLevelLink.label}
                <ExpandMore className={styles.chevron} />
              </button>
            ) : (
              <Link href={topLevelLink.url} prefetch={false}>
                {topLevelLink.label}
              </Link>
            )}

            {hasChildren && (
              <div className={styles.subMenuItems} data-is-expanded={isExpanded} hidden={!isExpanded}>
                {topLevelLink.childLinks?.map((secondLevelLink) => {
                  const hasThirdLevel = !!secondLevelLink.childLinks?.length;
                  const secondLevelKey = `${topLevelLink.label}:${secondLevelLink.label}`;
                  const isSecondLevelExpanded = expandedSecondLevels.has(secondLevelKey);

                  return (
                    <div
                      className={styles.subMenuItem}
                      data-is-active={isCurrentSection(secondLevelLink.url)}
                      data-is-expanded={isSecondLevelExpanded}
                      key={secondLevelLink.label}
                    >
                      {hasThirdLevel ? (
                        <button
                          type="button"
                          onClick={() => toggleSecondLevel(secondLevelKey)}
                          aria-expanded={isSecondLevelExpanded}
                        >
                          {secondLevelLink.label}
                          <ExpandMore className={styles.chevron} />
                        </button>
                      ) : (
                        <Link href={secondLevelLink.url} prefetch={false}>
                          {secondLevelLink.label}
                        </Link>
                      )}

                      {hasThirdLevel && (
                        <div
                          className={styles.thirdItems}
                          data-is-expanded={isSecondLevelExpanded}
                          hidden={!isSecondLevelExpanded}
                        >
                          {secondLevelLink.childLinks?.map((thirdLevelLink) => (
                            <Link
                              className={styles.thirdItem}
                              key={thirdLevelLink.label}
                              href={thirdLevelLink.url}
                              prefetch={false}
                            >
                              {thirdLevelLink.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
